import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { IonSpinner } from '@ionic/angular/standalone';
import { TranslateModule } from '@ngx-translate/core';
import Decimal from 'decimal.js';
import { debounceTime, filter, take } from 'rxjs';
import { DeciHelperComponent } from 'src/app/components/deci-helper/deci-helper.component';
import { LoaderComponent } from 'src/app/components/loader/loader.component';
import { PriceComponent } from 'src/app/components/price/price.component';
import { CurrencySymbolPipe } from 'src/app/pipes/currency-symbol.pipe';
import { getActiveMarket } from '../../../../config/markets.config';
import { CreateOfferData } from '../../../../models/create-offer/create-offer-data';
import { PriceData, PriceForm, SHIPPING_SUBSIDY_PERCENT_OPTIONS } from '../../../../models/create-offer/price-data';
import { Shipment } from '../../../../models/delivery';
import { PriceObject } from '../../../../models/offer';
import { OfferVariantPost } from '../../../../models/offer-definition';
import { AppPlatform } from '../../../../models/platform';
import { User } from '../../../../models/user';
import { Vat } from '../../../../models/vat';
import { AuthService } from '../../../../services/auth.service';
import { CurrencyService } from '../../../../services/currency.service';
import { DeliveryService } from '../../../../services/delivery.service';
import { OfferService } from '../../../../services/offer.service';
import { OrderService } from '../../../../services/order.service';
import { SkuValidatorService } from '../../../../services/sku-validator.service';
import { ToastService } from '../../../../services/toast.service';
import { CategoryUtils } from '../../../../utils/category.utils';
import { isVerticalAdPlatform } from '../../../../utils/platform.utils';
import { RegexUtils } from '../../../../utils/regex.utils';
import { ValidateWholesale, ValidateWholesalePriceLine } from '../../../../validators/wholesale-price.validator';
import { lowestEnteredVariantPrice } from './variant-reference-price';

type VariantLine = { form: FormGroup; name: string[]; code: string };

@Component({
    selector: 'app-pick-prices',
    templateUrl: './pick-prices.component.html',
    styleUrls: ['./pick-prices.component.scss'],
    imports: [
        CommonModule,
        CommonModule,
        ReactiveFormsModule,
        TranslateModule,
        DeciHelperComponent,
        LoaderComponent,
        PriceComponent,
        CurrencySymbolPipe,
        IonSpinner,
    ],
})
export class PickPricesComponent implements OnInit {
    @Input() data: CreateOfferData;
    /** When editing an offer, pass id so SKU async validation excludes this offer */
    @Input() editOfferId: number | null = null;
    @Input() isEdit = false;
    @Output() nextStep = new EventEmitter<CreateOfferData>();
    protected currencies = CurrencyService.currencies;
    protected form: FormGroup<PriceForm> = new FormGroup(
        {
            referencePrice: new FormControl(null, [Validators.required, Validators.min(1)]),
            referenceCurrency: new FormControl(null, Validators.required),
            vat: new FormControl(null),
            variants: new FormGroup({}),
        },
        [Validators.required, ValidateWholesale],
    );
    private platform: AppPlatform;
    protected shippable: boolean;
    protected seller: User;
    protected isWholeSale: boolean;
    protected vats: Vat[];
    protected variants: OfferVariantPost[] = [];
    protected hasVariantPrices: boolean;
    protected hasVariants: boolean;
    protected loading = true;
    /** True after Next while the form is invalid, so fields paint without blur. */
    protected submitted = false;
    /**
     * Wholesale restore writes min quantity with emitEvent: false, then the view
     * can emit it again. Keep the grid listener quiet until that second pass.
     */
    private seedingWholesale = false;
    protected variantUniquePrice: boolean = false;
    protected deliveryPriceEstimate: PriceObject;
    /** Gross carrier cost (API); kept for clarity when showing buyer share. */
    protected buyerDeliveryPriceEstimate: PriceObject | null = null;
    /** Seller's net share of the shipping cost after the subsidy is applied. */
    protected sellerDeliveryPriceEstimate: PriceObject | null = null;
    protected deliveryPriceEstimateLoading = false;
    protected readonly shippingSubsidyPercentOptions = SHIPPING_SUBSIDY_PERCENT_OPTIONS;
    protected hasStocks: boolean;
    protected hasSku: boolean;
    protected variantLines: VariantLine[] = [];
    private skuLoadingMap = new Map<string, boolean>();

    constructor(
        private readonly currencyService: CurrencyService,
        private readonly authService: AuthService,
        private readonly orderService: OrderService,
        private readonly deliveryService: DeliveryService,
        private readonly offerService: OfferService,
        private readonly toastService: ToastService,
        private readonly skuValidatorService: SkuValidatorService,
        private readonly cdr: ChangeDetectorRef,
    ) {}

    ngOnInit() {
        this.authService.userLoggedIn
            .pipe(
                filter((user) => !!user),
                take(1),
            )
            .subscribe({
                next: (user) => {
                    this.platform = this.data.platformData.platform;
                    this.isWholeSale = this.platform === 'wholesale';
                    this.hasVariants = this.data.filterData.variants?.length > 0;
                    this.hasStocks = this.platform !== 'gazette' && !isVerticalAdPlatform(this.platform);
                    this.variants = this.data.filterData.variants;
                    this.hasVariantPrices = this.hasVariants && !this.isWholeSale;
                    this.shippable = CategoryUtils.findProperty(this.data.categoryData.category, 'shippable');
                    this.hasSku = this.shippable && this.platform !== 'gazette' && !isVerticalAdPlatform(this.platform);
                    this.seller = user;

                    // Set fields
                    this.setFormsFields();

                    // Update VAT validation based on visibility
                    this.updateVatValidation();

                    // Activate wholesale prices
                    if (this.isWholeSale) {
                        this.enableWholesalePrices();
                    } else {
                        this.disableWholesalePrices();
                    }

                    // Create variant forms
                    if (this.hasVariants) {
                        this.createVariantForms();
                    }

                    // Add shipping fields
                    if (this.shippable) {
                        this.addShippingFields();
                    }

                    this.seedingWholesale = this.isWholeSale && this.hasStoredWholesalePrices(this.data?.priceData);
                    this.seedFromData();
                    this.loading = false;
                    // Native selects render after loading flips. Re-apply once they exist,
                    // then let the min-quantity listener maintain the grid.
                    setTimeout(() => {
                        this.seedFromData();
                        this.seedingWholesale = false;
                        this.cdr.markForCheck();
                    });
                },
            });
        // Set vats — re-patch VAT once options exist (async load + select needs ngValue)
        this.orderService.getVats(getActiveMarket().country).subscribe({
            next: (vats) => {
                if (vats && vats.length > 0) {
                    this.vats = vats;
                    this.patchVatFromData();
                    this.cdr.markForCheck();
                }
            },
        });
    }

    /** Re-apply stored VAT after the async VAT list loads (edit / back-navigation). */
    private patchVatFromData(): void {
        const vatId = this.data?.priceData?.vat;
        if (vatId == null || !this.form.contains('vat')) {
            return;
        }
        this.form.get('vat')?.setValue(+vatId);
    }

    enableWholesalePrices() {
        this.form.addControl('minWholesaleQuantity', new FormControl(null, [Validators.required, Validators.min(2)]));
        this.form.addControl('samplePrice', new FormControl(null, [Validators.required, Validators.min(1)]));
        this.form.addControl('wholesalePrices', new FormArray([], [Validators.required, Validators.minLength(1)]));
        this.form.removeControl('referencePrice');
        // Listen on change event of priceTab
        this.form.get('minWholesaleQuantity').valueChanges.subscribe({
            next: (quantity: number) => {
                if (!quantity || this.seedingWholesale) {
                    return;
                }

                const wholesalePrices = this.form.get('wholesalePrices') as FormArray;
                const nextMin = Number(quantity) + 1;
                // Create a first line for wholesalePrices if not exists
                if (!wholesalePrices || wholesalePrices.length === 0) {
                    this.addWholesalePrice(nextMin);
                    return;
                }
                // Same minimum must not rebuild or rewrite the restored grid.
                const minControl = wholesalePrices.at(0).get('min');
                if (Number(minControl?.value) === nextMin) {
                    return;
                }
                minControl?.setValue(nextMin);
            },
        });
    }

    private addShippingFields() {
        this.form.addControl('packages', new FormArray([], [Validators.required, Validators.minLength(1)]));
        this.form.addControl('daysToShip', new FormControl(null, [Validators.required, Validators.min(0), Validators.max(3)]));
        this.form.addControl('shippingSubsidyPercent', new FormControl(0, [Validators.required, Validators.min(0), Validators.max(100)]));
        this.form.get('shippingSubsidyPercent').valueChanges.subscribe({
            next: () => this.updateSharedDeliveryPriceEstimate(),
        });
        this.addPackage();
    }

    /**
     * When reusing this step for offer edit (or re-entering it after a back
     * navigation), priceData is pre-filled; patch the form once controls exist.
     * Mirrors the seed pattern used by pick-filters / pick-medias / pick-addresses.
     */
    private seedFromData(): void {
        const pd = this.data?.priceData;
        if (!pd) {
            return;
        }
        if (this.isWholeSale) {
            this.seedWholesaleFields(pd);
        } else if (pd.referenceCurrency != null || pd.referencePrice != null || pd.vat != null) {
            this.form.patchValue(
                {
                    referencePrice: pd.referencePrice != null ? +pd.referencePrice : null,
                    referenceCurrency: Array.isArray(pd.referenceCurrency) ? pd.referenceCurrency[0] : pd.referenceCurrency,
                    vat: pd.vat != null ? +pd.vat : null,
                },
                { emitEvent: false },
            );
        }
        if (this.shippable && this.form.contains('daysToShip') && pd.daysToShip != null) {
            this.form.get('daysToShip')?.patchValue(pd.daysToShip, { emitEvent: false });
        }
        if (this.shippable && this.form.contains('shippingSubsidyPercent') && pd.shippingSubsidyPercent != null) {
            this.form.get('shippingSubsidyPercent')?.patchValue(pd.shippingSubsidyPercent, { emitEvent: false });
        }
        if (this.shippable && this.form.contains('packages') && pd.packages?.length) {
            const arr = this.form.get('packages') as FormArray;
            while (arr.length > 0) {
                arr.removeAt(0);
            }
            for (const p of pd.packages) {
                this.addPackage();
                const last = arr.at(arr.length - 1) as FormGroup;
                last.patchValue({ weight: p.weight, deci: p.deci != null ? +p.deci : null }, { emitEvent: false });
            }
        }
        if (!this.hasVariants) {
            if (this.form.contains('stocks') && pd.stocks != null) {
                this.form.get('stocks')?.patchValue(pd.stocks, { emitEvent: false });
            }
            if (this.form.contains('sku') && pd.sku != null) {
                this.form.get('sku')?.patchValue(pd.sku, { emitEvent: false });
            }
            if (this.shippable) {
                this.refreshDeliveryPriceEstimate();
            }
            return;
        }
        for (const line of this.variantLines) {
            const v = pd.variants?.[line.code];
            if (!v) {
                continue;
            }
            const fg = line.form;
            // Do not emit while later lines are still empty: that change used to
            // clear referencePrice before the rest of the prices were restored.
            if (v.price != null && fg.contains('price')) {
                fg.get('price')?.patchValue(+v.price, { emitEvent: false });
            }
            if (v.stocks != null && fg.contains('stocks')) {
                fg.get('stocks')?.patchValue(v.stocks, { emitEvent: false });
            }
            if (v.sku != null && fg.contains('sku')) {
                fg.get('sku')?.patchValue(v.sku, { emitEvent: false });
            }
        }
        this.syncReferencePriceFromVariants();
        if (this.shippable) {
            this.refreshDeliveryPriceEstimate();
        }
    }

    /** Saved wholesale rows must be restored before the min-quantity listener can rebuild them. */
    private hasStoredWholesalePrices(pd: PriceData | undefined): boolean {
        if (!pd) {
            return false;
        }
        return pd.minWholesaleQuantity != null || pd.samplePrice != null || (pd.wholesalePrices?.length ?? 0) > 0;
    }

    /**
     * Put sample price, currency, VAT, minimum quantity and every price-grid
     * line back without letting the min-quantity listener rebuild the grid.
     */
    private seedWholesaleFields(pd: PriceData): void {
        if (pd.referenceCurrency != null && this.form.contains('referenceCurrency')) {
            const currency = Array.isArray(pd.referenceCurrency) ? pd.referenceCurrency[0] : pd.referenceCurrency;
            this.form.get('referenceCurrency')?.setValue(currency, { emitEvent: false });
        }
        if (pd.vat != null && this.form.contains('vat')) {
            this.form.get('vat')?.setValue(+pd.vat, { emitEvent: false });
        }
        if (pd.samplePrice != null && this.form.contains('samplePrice')) {
            this.form.get('samplePrice')?.setValue(+pd.samplePrice, { emitEvent: false });
        }
        if (pd.minWholesaleQuantity != null && this.form.contains('minWholesaleQuantity')) {
            this.form.get('minWholesaleQuantity')?.setValue(+pd.minWholesaleQuantity, { emitEvent: false });
        }
        if (pd.wholesalePrices?.length && this.form.contains('wholesalePrices')) {
            this.replaceWholesalePriceLines(pd.wholesalePrices);
        }
    }

    private replaceWholesalePriceLines(lines: PriceData['wholesalePrices']): void {
        const arr = this.form.get('wholesalePrices') as FormArray;
        while (arr.length > 0) {
            arr.removeAt(0);
        }
        for (const line of lines) {
            const min = line.min != null ? +line.min : null;
            this.addWholesalePrice(min);
            const group = arr.at(arr.length - 1) as FormGroup;
            if (min != null) {
                group.get('min')?.setValue(min, { emitEvent: false });
            }
            group.get('max')?.setValue(line.max != null ? +line.max : null, { emitEvent: false });
            group.get('price')?.setValue(line.price != null && line.price !== '' ? +line.price : null, { emitEvent: false });
        }
    }

    disableWholesalePrices() {
        // Remove wholesale prices controls and add reference price
        this.form.removeControl('minWholesaleQuantity');
        this.form.removeControl('samplePrice');
        this.form.removeControl('wholesalePrices');
        this.form.addControl('referencePrice', new FormControl(null, [Validators.required, Validators.min(1)]));
    }

    getWholesaleForms(): FormGroup[] {
        // return [];
        return (this.form.get('wholesalePrices') as FormArray).controls as FormGroup[];
    }

    addWholesalePrice(min: number = null) {
        // If min is null, we take the max of the last line + 1
        if (!min) {
            const wholesalePrices = this.form.get('wholesalePrices') as FormArray;
            if (wholesalePrices.controls.length > 0) {
                const lastLine = wholesalePrices.controls[wholesalePrices.controls.length - 1];
                const max = lastLine.get('max').value;
                if (max) {
                    min = max + 1;
                }
            }
        }
        const wholesalePrices = this.form.get('wholesalePrices') as FormArray;
        const formGroup = new FormGroup(
            {
                min: new FormControl({ value: min, disabled: true }, [Validators.required, Validators.min(1)]),
                max: new FormControl(null, [Validators.required, Validators.min(1)]),
                price: new FormControl(null, [Validators.required, Validators.min(1)]),
            },
            [Validators.required, ValidateWholesalePriceLine],
        );
        wholesalePrices.push(formGroup);
        // List on max value change to update the next min value
        formGroup.get('max').valueChanges.subscribe({
            next: (max) => {
                if (max) {
                    const wholesalePrices = this.form.get('wholesalePrices') as FormArray;
                    const index = wholesalePrices.controls.indexOf(formGroup);
                    if (index < wholesalePrices.controls.length - 1) {
                        wholesalePrices.controls[index + 1].get('min').setValue(max + 1);
                    }
                }
            },
        });
    }

    removeWholesalePrice(form: FormGroup) {
        const wholesalePrices = this.form.get('wholesalePrices') as FormArray;
        wholesalePrices.removeAt(wholesalePrices.controls.indexOf(form));
    }

    isInvalid(field: string) {
        return this.form.get(field)?.invalid && this.form.get(field)?.touched;
    }

    /**
     * In-form barcode uniqueness stays on the variants group.
     * This only decides which lines should show that error.
     */
    isDuplicateVariantSku(line: VariantLine): boolean {
        if (!this.hasSku) {
            return false;
        }
        const sku = line.form.get('sku')?.value;
        if (!sku) {
            return false;
        }
        const variants = this.form.get('variants') as FormGroup | null;
        if (!variants) {
            return false;
        }
        const matches = Object.values(variants.controls).filter((group) => group.get('sku')?.value === sku);
        return matches.length > 1;
    }

    /**
     * Other variant fields turn red once touched. A repeated barcode turns red
     * immediately, with the same is-invalid border as the other field errors.
     */
    isVariantSkuInvalid(line: VariantLine): boolean {
        const sku = line.form.get('sku');
        if (!sku) {
            return false;
        }
        if (this.isDuplicateVariantSku(line)) {
            return true;
        }
        return sku.invalid && sku.touched;
    }

    /**
     * Cross-field errors are written onto the price and max controls.
     * Paint them once Next was clicked or the row was touched.
     */
    isWholesaleFieldInvalid(wholesaleForm: FormGroup, field: string): boolean {
        const control = wholesaleForm.get(field);
        if (!control) {
            return false;
        }
        const visible = this.submitted || control.touched || wholesaleForm.touched;
        return visible && control.invalid;
    }

    showWholesaleFieldError(wholesaleForm: FormGroup, field: string, errorCode: string): boolean {
        const control = wholesaleForm.get(field);
        if (!control?.hasError(errorCode)) {
            return false;
        }
        return this.submitted || control.touched || wholesaleForm.touched;
    }

    onSkuInput(control: AbstractControl | null, event: Event): void {
        const input = event.target as HTMLInputElement | null;
        if (!input || !control) {
            return;
        }
        this.commitSkuValue(control, input, input.value, input.selectionStart ?? input.value.length);
    }

    onSkuPaste(control: AbstractControl | null, event: ClipboardEvent): void {
        const input = event.target as HTMLInputElement | null;
        if (!input || !control) {
            return;
        }
        event.preventDefault();
        const pasted = (event.clipboardData?.getData('text') ?? '').replace(/\s/g, '');
        const start = input.selectionStart ?? input.value.length;
        const end = input.selectionEnd ?? start;
        const current = input.value.replace(/\s/g, '');
        const next = `${current.slice(0, start)}${pasted}${current.slice(end)}`;
        this.commitSkuValue(control, input, next, start + pasted.length);
    }

    private commitSkuValue(control: AbstractControl, input: HTMLInputElement, raw: string, caret: number): void {
        const stripped = raw.replace(/\s/g, '');
        const nextCaret = raw.slice(0, caret).replace(/\s/g, '').length;
        if (input.value !== stripped) {
            input.value = stripped;
        }
        if (control.value !== stripped) {
            control.setValue(stripped);
        }
        input.setSelectionRange(nextCaret, nextCaret);
    }

    createVariantForms() {
        const variantLine: VariantLine[] = [];
        for (const variant of this.variants) {
            variantLine.push({
                form: this.addVariantForm(variant),
                name: variant.name,
                code: variant.code,
            });
        }
        if (this.hasSku) {
            this.form.get('variants').setValidators(this.skuValidatorService.createUniqueSkuInFormValidator());
        }
        if (this.hasVariants && this.hasVariantPrices) {
            this.form.get('variants').valueChanges.subscribe({
                next: (value) => {
                    // Copy first, then read getRawValue(). The event value is stale:
                    // setUniquePriceForAllVariants writes with emitEvent: false.
                    if (this.variantUniquePrice) {
                        const values = Object.values(value);
                        this.setUniquePriceForAllVariants(values[0].price);
                    }
                    this.syncReferencePriceFromVariants();
                },
            });
        }
        this.variantLines = variantLine;
    }

    private setUniquePriceForAllVariants(price: number) {
        this.variantLines.forEach((line, index) => {
            if (index !== 0) {
                line.form.get('price').setValue(price, { emitEvent: false });
            }
        });
    }

    /**
     * Hidden referencePrice is the lowest price actually entered on a variant line.
     * Prix unique disables every price after the first, so this must read getRawValue().
     */
    private syncReferencePriceFromVariants(): void {
        const referencePrice = this.form.get('referencePrice');
        if (!referencePrice) {
            return;
        }
        const variants = (this.form.get('variants') as FormGroup).getRawValue() as Record<string, { price?: unknown }>;
        referencePrice.setValue(lowestEnteredVariantPrice(variants), { emitEvent: false });
    }

    addVariantForm(variant: OfferVariantPost): FormGroup {
        const variants = this.form.get('variants') as FormGroup;
        const variantFormGroup: FormGroup = new FormGroup({
            stocks: new FormControl(null, [Validators.required, Validators.min(0), Validators.pattern(RegexUtils.integer)]),
            sku: new FormControl(null, [Validators.required, Validators.pattern(RegexUtils.sku)]),
        });

        // Add async validator to SKU control if we have SKUs
        if (this.hasSku) {
            const skuControl = variantFormGroup.get('sku');
            skuControl.setAsyncValidators(
                this.skuValidatorService.createUniqueSkuAcrossOffersValidator(
                    this.editOfferId != null ? String(this.editOfferId) : undefined,
                ),
            );
        }

        if (this.hasVariantPrices) {
            variantFormGroup.addControl('price', new FormControl(null, [Validators.required, Validators.min(1)]));
        }
        variants.addControl(variant.code, variantFormGroup); // variant.code, variantFormGroup);

        return variantFormGroup;
    }

    setFormsFields(): void {
        // If we don't have variants, we need to add the stock field
        if (!this.hasVariants) {
            if (this.hasStocks) {
                this.form.addControl(
                    'stocks',
                    new FormControl(null, [Validators.required, Validators.min(0), Validators.pattern(RegexUtils.integer)]),
                );
            }
            if (this.hasSku) {
                this.form.addControl('sku', new FormControl(null, [Validators.required]));
                this.form
                    .get('sku')
                    .setAsyncValidators(
                        this.skuValidatorService.createUniqueSkuAcrossOffersValidator(
                            this.editOfferId != null ? String(this.editOfferId) : undefined,
                        ),
                    );
            }
        } else {
            this.form.removeControl('stocks');
            this.form.removeControl('sku');
        }
        // Listen on change of referenceCurrency to refresh the delivery price estimate
        this.form.get('referenceCurrency').valueChanges.subscribe({
            next: () => {
                this.refreshDeliveryPriceEstimate();
            },
        });
    }

    private updateSharedDeliveryPriceEstimate(): void {
        if (!this.deliveryPriceEstimate) {
            this.buyerDeliveryPriceEstimate = null;
            this.sellerDeliveryPriceEstimate = null;
            return;
        }
        const raw = this.form.get('shippingSubsidyPercent')?.value;
        const subsidy = Math.min(100, Math.max(0, Number(raw ?? 0)));
        const buyerFactor = new Decimal(100 - subsidy).div(100);
        const sellerFactor = new Decimal(subsidy).div(100);
        this.buyerDeliveryPriceEstimate = {
            price: this.deliveryPriceEstimate.price.mul(buyerFactor),
            currency: this.deliveryPriceEstimate.currency,
            from: false,
        };
        this.sellerDeliveryPriceEstimate = {
            price: this.deliveryPriceEstimate.price.mul(sellerFactor),
            currency: this.deliveryPriceEstimate.currency,
            from: false,
        };
        this.cdr.markForCheck();
    }

    addPackage() {
        const packages = this.form.get('packages') as FormArray;
        const packageFormGroup: FormGroup = new FormGroup({
            deci: new FormControl(null, [Validators.required, Validators.min(0.1), Validators.max(30)]),
            weight: new FormControl(null, [Validators.required, Validators.min(1), Validators.max(30000)]),
        });
        packages.push(packageFormGroup);

        // Listen on change of weight and deci to update the delivery price estimate
        packageFormGroup
            .get('deci')
            .valueChanges.pipe(debounceTime(400))
            .subscribe({
                next: () => {
                    this.refreshDeliveryPriceEstimate();
                },
            });
        packageFormGroup
            .get('weight')
            .valueChanges.pipe(debounceTime(400))
            .subscribe({
                next: () => {
                    this.refreshDeliveryPriceEstimate();
                },
            });
    }

    removePackage(index: number) {
        // We can't remove the last package
        if (index === 0 && (this.form.get('packages') as FormArray).controls.length === 1) {
            return;
        }
        const packages = this.form.get('packages') as FormArray;
        packages.removeAt(index);
        // Refresh delivery price estimate
        this.refreshDeliveryPriceEstimate();
    }

    getPackages(): FormGroup[] {
        return (this.form.get('packages') as FormArray).controls as FormGroup[];
    }

    toggleSamePriceForAllVariants() {
        this.variantUniquePrice = !this.variantUniquePrice;
        // Update the form to disable the price field for each variant if variantUniquePrice is true except for the first one
        this.variantLines.forEach((line, index) => {
            if (index !== 0) {
                if (this.variantUniquePrice) {
                    line.form.get('price').disable({ onlySelf: true, emitEvent: false });
                } else {
                    line.form.get('price').enable({ onlySelf: true, emitEvent: false });
                }
            }
        });
        const price = this.variantLines[0].form.get('price').value;
        this.setUniquePriceForAllVariants(price);
        this.syncReferencePriceFromVariants();
    }

    refreshDeliveryPriceEstimate() {
        if (!this.shippable) {
            return;
        }

        this.deliveryPriceEstimateLoading = true;
        const currency = this.form.get('referenceCurrency').value;

        // Compute total weight and deci
        const market = getActiveMarket();
        const shipments: Shipment[] = (this.form.get('packages') as FormArray).controls.map((control) => ({
            deci: (control.get('deci').value as string) || '0',
            weight: (control.get('weight').value as number) || 0,
            provider: market.carrierName,
            daysToShip: this.form.get('daysToShip').value || 1,
        }));

        this.deliveryService
            .getDeliveryCosts({
                shipments,
                currency,
                originCountry: market.country,
                destinationCountry: market.country,
            })
            .subscribe({
                next: (response) => {
                    const price = response.reduce((prev, curr) => prev.add(new Decimal(curr.cost)), new Decimal(0));
                    this.deliveryPriceEstimate = {
                        price,
                        currency: response[0].currency,
                        from: false,
                    };
                    this.updateSharedDeliveryPriceEstimate();
                    this.deliveryPriceEstimateLoading = false;
                },
            });
    }

    shouldShowVat(): boolean {
        if (isVerticalAdPlatform(this.platform)) {
            return false;
        }
        return !(this.platform === 'gazette' && !this.shippable);
    }

    updateVatValidation(): void {
        const vatControl = this.form.get('vat');
        if (this.shouldShowVat()) {
            vatControl.setValidators(Validators.required);
        } else {
            vatControl.clearValidators();
            vatControl.setValue(null);
        }
        vatControl.updateValueAndValidity();
    }

    async submit() {
        if (this.form.invalid) {
            this.submitted = true;
            this.form.markAllAsTouched();
            this.cdr.markForCheck();
            return;
        }

        // All good, we can go to the next step
        this.loading = true;

        // getRawValue() keeps disabled controls: unique-price lines and wholesale min.
        const values = this.form.getRawValue();
        this.data.priceData.referencePrice = values.referencePrice;
        this.data.priceData.referenceCurrency = values.referenceCurrency;
        this.data.priceData.vat = values.vat ? +values.vat : null;
        this.data.priceData.variants = values.variants;
        this.data.priceData.stocks = values.stocks || (this.platform === 'gazette' || isVerticalAdPlatform(this.platform) ? 1 : 0);
        this.data.priceData.sku = values.sku ?? null;
        this.data.priceData.daysToShip = values.daysToShip;
        this.data.priceData.shippingSubsidyPercent = values.shippingSubsidyPercent ?? 0;
        this.data.priceData.packages = values.packages;
        this.data.priceData.wholesalePrices = values.wholesalePrices;
        this.data.priceData.minWholesaleQuantity = values.minWholesaleQuantity;
        this.data.priceData.samplePrice = values.samplePrice;

        this.nextStep.emit(this.data);
    }

    /**
     * Checks if a SKU is currently being validated
     *
     * @param sku The SKU to check
     * @returns True if the SKU is being validated, false otherwise
     */
    isSkuLoading(sku: string): boolean {
        if (!sku) {
            return false;
        }

        // Subscribe to the loading state if not already subscribed
        if (!this.skuLoadingMap.has(sku)) {
            this.skuValidatorService.isSkuLoading(sku).subscribe((isLoading) => {
                this.skuLoadingMap.set(sku, isLoading);
            });
        }

        return this.skuLoadingMap.get(sku) || false;
    }
}
